import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useOrgSubscription } from '../hooks/useOrgSubscription';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { ArrowLeft, Loader2, Plus, Trash2, GripVertical, Settings, Code2 } from 'lucide-react';
import { toast } from 'sonner';
import { API_URL, BACKEND_URL } from '../config';

const emptyItem = () => ({ question: '', answer: '' });

const FaqSetEditPage = () => {
  const { setId } = useParams();
  const isNew = setId === 'new';
  const setIdMissing = !setId || setId === 'undefined';
  const navigate = useNavigate();
  const { currentOrg } = useAuth();
  const { subscription, loading: subLoading } = useOrgSubscription(currentOrg?.id);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [description, setDescription] = useState('');
  const [items, setItems] = useState([emptyItem()]);
  const [style, setStyle] = useState({
    theme: 'minimal',
    accordion_mode: 'multiple',
    accent_color: '',
  });
  const [publicEmbedId, setPublicEmbedId] = useState('');
  const [styleOpen, setStyleOpen] = useState(false);
  const [embedOpen, setEmbedOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const addonOn = !!subscription?.faqs_addon?.enabled;
  const canEdit =
    currentOrg?.user_role && ['company', 'member'].includes(currentOrg.user_role);

  const load = useCallback(async () => {
    if (!currentOrg?.id || isNew || setIdMissing) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/organizations/${currentOrg.id}/faq-sets/${setId}`);
      const d = res.data;
      setTitle(d.title || '');
      setSubtitle(d.subtitle || '');
      setDescription(d.description || '');
      setItems(
        d.items?.length
          ? d.items.map((it) => ({ question: it.question || '', answer: it.answer || '' }))
          : [emptyItem()]
      );
      setStyle({
        theme: d.style?.theme || 'minimal',
        accordion_mode: d.style?.accordion_mode || 'multiple',
        accent_color: d.style?.accent_color || '',
      });
      setPublicEmbedId(d.public_embed_id || '');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load FAQ set');
      navigate('/dashboard/faqs');
    } finally {
      setLoading(false);
    }
  }, [currentOrg?.id, isNew, navigate, setId, setIdMissing]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (setIdMissing && !isNew) {
      navigate('/dashboard/faqs', { replace: true });
    }
  }, [setIdMissing, isNew, navigate]);

  const persistStyle = async (nextStyle) => {
    if (!currentOrg?.id || isNew) {
      setStyle(nextStyle);
      return;
    }
    try {
      await axios.put(`${API_URL}/organizations/${currentOrg.id}/faq-sets/${setId}`, {
        style: nextStyle,
      });
      setStyle(nextStyle);
      toast.success('Appearance saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save appearance');
    }
  };

  const handleSave = async () => {
    if (!currentOrg?.id || !canEdit) return;
    if (!title.trim()) {
      toast.error('Title is required');
      return;
    }
    const cleaned = items
      .map((it) => ({
        question: it.question.trim(),
        answer: it.answer.trim(),
      }))
      .filter((it) => it.question || it.answer);
    if (!cleaned.length) {
      toast.error('Add at least one question with an answer');
      return;
    }
    setSaving(true);
    try {
      if (isNew) {
        const res = await axios.post(`${API_URL}/organizations/${currentOrg.id}/faq-sets`, {
          title: title.trim(),
          subtitle: subtitle.trim(),
          description: description.trim(),
          items: cleaned,
          style,
        });
        const newId = res.data?.id;
        if (!newId) {
          toast.error('Created FAQ set but response had no id — check API');
          return;
        }
        toast.success('FAQ set created');
        navigate(`/dashboard/faqs/${newId}`, { replace: true });
      } else {
        await axios.put(`${API_URL}/organizations/${currentOrg.id}/faq-sets/${setId}`, {
          title: title.trim(),
          subtitle: subtitle.trim(),
          description: description.trim(),
          items: cleaned,
          style,
        });
        toast.success('Saved');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!currentOrg?.id || isNew) return;
    try {
      await axios.delete(`${API_URL}/organizations/${currentOrg.id}/faq-sets/${setId}`);
      toast.success('Deleted');
      navigate('/dashboard/faqs');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    const t = next[idx];
    next[idx] = next[j];
    next[j] = t;
    setItems(next);
  };

  if (loading) {
    return (
      <div className="p-8 flex justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  const iframeSrc = publicEmbedId
    ? `${typeof window !== 'undefined' ? window.location.origin : ''}/embed/faq/${publicEmbedId}`
    : '';
  const scriptSnippet = publicEmbedId
    ? `<script src="${BACKEND_URL}/api/public/faq-widget.js" data-public-id="${publicEmbedId}" data-min-height="520" async><\/script>`
    : '';

  return (
    <div className="p-8 max-w-3xl mx-auto space-y-6">
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard/faqs')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          All FAQs
        </Button>
        {!isNew && publicEmbedId ? (
          <>
            <Button variant="outline" size="sm" onClick={() => setEmbedOpen(true)}>
              <Code2 className="w-4 h-4 mr-1" />
              Embed codes
            </Button>
            <Button variant="outline" size="sm" asChild>
              <a href={`/embed/faq/${publicEmbedId}`} target="_blank" rel="noreferrer">
                Preview
              </a>
            </Button>
          </>
        ) : null}
        <div className="flex-1" />
        {!isNew ? (
          <Button variant="outline" size="sm" onClick={() => setStyleOpen(true)}>
            <Settings className="w-4 h-4 mr-1" />
            Appearance
          </Button>
        ) : null}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="font-[Manrope]">{isNew ? 'New FAQ set' : 'Edit FAQ set'}</CardTitle>
          <CardDescription>Header, questions, and answers shown in the embedded accordion.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="faq-title">Title</Label>
            <Input
              id="faq-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Billing & subscriptions"
              disabled={!canEdit}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="faq-sub">Subtitle (optional)</Label>
            <Input
              id="faq-sub"
              value={subtitle}
              onChange={(e) => setSubtitle(e.target.value)}
              placeholder="Short line under the title"
              disabled={!canEdit}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="faq-desc">Description (optional)</Label>
            <Textarea
              id="faq-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              placeholder="Intro text above the accordion"
              disabled={!canEdit}
            />
          </div>

          {isNew ? (
            <div className="rounded-lg border border-slate-200 dark:border-slate-800 p-4 space-y-3">
              <div className="text-sm font-medium">Appearance</div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Theme</Label>
                  <Select
                    value={style.theme}
                    onValueChange={(v) => setStyle((s) => ({ ...s, theme: v }))}
                    disabled={!canEdit}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="minimal">Minimal</SelectItem>
                      <SelectItem value="bordered">Bordered</SelectItem>
                      <SelectItem value="card">Card</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Accordion</Label>
                  <Select
                    value={style.accordion_mode}
                    onValueChange={(v) => setStyle((s) => ({ ...s, accordion_mode: v }))}
                    disabled={!canEdit}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="multiple">Allow multiple open</SelectItem>
                      <SelectItem value="single">One section at a time</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Accent color (optional hex)</Label>
                <Input
                  value={style.accent_color}
                  onChange={(e) => setStyle((s) => ({ ...s, accent_color: e.target.value }))}
                  placeholder="#0f766e"
                  disabled={!canEdit}
                />
              </div>
            </div>
          ) : null}

          <div className="space-y-3 pt-2">
            <div className="flex items-center justify-between">
              <Label>Questions & answers</Label>
              {canEdit ? (
                <Button type="button" variant="outline" size="sm" onClick={() => setItems([...items, emptyItem()])}>
                  <Plus className="w-4 h-4 mr-1" />
                  Add question
                </Button>
              ) : null}
            </div>
            {items.map((it, idx) => (
              <div
                key={idx}
                className="border border-slate-200 dark:border-slate-800 rounded-lg p-4 space-y-3 bg-slate-50/50 dark:bg-slate-900/40"
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1 text-xs text-slate-500">
                    <GripVertical className="w-4 h-4" />
                    Item {idx + 1}
                  </div>
                  {canEdit ? (
                    <div className="flex items-center gap-1">
                      <Button type="button" variant="ghost" size="sm" onClick={() => move(idx, -1)}>
                        Up
                      </Button>
                      <Button type="button" variant="ghost" size="sm" onClick={() => move(idx, 1)}>
                        Down
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-red-600"
                        onClick={() => {
                          if (items.length <= 1) {
                            setItems([emptyItem()]);
                          } else {
                            setItems(items.filter((_, i) => i !== idx));
                          }
                        }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : null}
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Question</Label>
                  <Input
                    value={it.question}
                    onChange={(e) => {
                      const next = [...items];
                      next[idx] = { ...next[idx], question: e.target.value };
                      setItems(next);
                    }}
                    disabled={!canEdit}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Answer</Label>
                  <Textarea
                    rows={4}
                    value={it.answer}
                    onChange={(e) => {
                      const next = [...items];
                      next[idx] = { ...next[idx], answer: e.target.value };
                      setItems(next);
                    }}
                    disabled={!canEdit}
                  />
                </div>
              </div>
            ))}
          </div>

          {canEdit ? (
            <div className="flex flex-wrap gap-2 pt-4">
              <Button onClick={handleSave} disabled={saving}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {isNew ? 'Create' : 'Save changes'}
              </Button>
              {!isNew ? (
                <Button type="button" variant="outline" className="text-red-600" onClick={() => setDeleteOpen(true)}>
                  Delete set
                </Button>
              ) : null}
            </div>
          ) : null}
        </CardContent>
      </Card>

      <Dialog open={styleOpen} onOpenChange={setStyleOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Appearance</DialogTitle>
            <DialogDescription>Controls how the widget looks when embedded.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1">
              <Label>Theme</Label>
              <Select
                value={style.theme}
                onValueChange={(v) => setStyle((s) => ({ ...s, theme: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="minimal">Minimal</SelectItem>
                  <SelectItem value="bordered">Bordered</SelectItem>
                  <SelectItem value="card">Card</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Accordion behavior</Label>
              <Select
                value={style.accordion_mode}
                onValueChange={(v) => setStyle((s) => ({ ...s, accordion_mode: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="multiple">Multiple sections open</SelectItem>
                  <SelectItem value="single">Single open section</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Accent color (optional)</Label>
              <Input
                value={style.accent_color}
                onChange={(e) => setStyle((s) => ({ ...s, accent_color: e.target.value }))}
                placeholder="#0f766e"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              onClick={async () => {
                await persistStyle(style);
                setStyleOpen(false);
              }}
            >
              Save appearance
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={embedOpen} onOpenChange={setEmbedOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Embed codes</DialogTitle>
            <DialogDescription>Iframe and script options for external sites.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 text-sm">
            <div>
              <div className="font-medium mb-1">Iframe</div>
              <pre className="text-xs bg-slate-950 text-slate-100 p-3 rounded-md overflow-x-auto whitespace-pre-wrap break-all">
                {`<iframe src="${iframeSrc}" title="FAQ" width="100%" style="border:0;min-height:520px" loading="lazy"></iframe>`}
              </pre>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                className="mt-2"
                onClick={() => {
                  navigator.clipboard.writeText(
                    `<iframe src="${iframeSrc}" title="FAQ" width="100%" style="border:0;min-height:520px" loading="lazy"></iframe>`
                  );
                  toast.success('Copied');
                }}
              >
                Copy iframe
              </Button>
            </div>
            <div>
              <div className="font-medium mb-1">Script</div>
              <pre className="text-xs bg-slate-950 text-slate-100 p-3 rounded-md overflow-x-auto whitespace-pre-wrap break-all">
                {scriptSnippet}
              </pre>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                className="mt-2"
                onClick={() => {
                  navigator.clipboard.writeText(scriptSnippet);
                  toast.success('Copied');
                }}
              >
                Copy script
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete this FAQ set?</DialogTitle>
            <DialogDescription>Embeds will stop working. This cannot be undone.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FaqSetEditPage;
